import OssDeviceSearch from "@/models/search/OssDeviceSearch";
import { getOssDeviceNamesWithPagination } from "@/services/ossDeviceService";
import { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../../../components/tabs/table-tab";
import { FILTER_DATE } from "../../../constants/filter-types";
import { REPORTS_TRANSACTION_ALARM } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { DEF_PG_SIZE_REPORTS } from '../../../constants/maximum-page-size';
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import TransactionColumn from "../../../models/ColumnDefinitions/TransactionColumn";
import { Filter } from "../../../models/Filter";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import BuildingSearch from "../../../models/search/BuildingSearch";
import CompanySearch from "../../../models/search/CompanySearch";
import ControllerSearch from "../../../models/search/ControllerSearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import DoorSearch from "../../../models/search/DoorSearch";
import EnrolmentSearch from "../../../models/search/EnrolmentSearch";
import EventTypeSearch from "../../../models/search/EventTypeSearch";
import ReaderSearch from "../../../models/search/ReaderSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import TransactionSearch from "../../../models/search/TransactionSearch";
import ZoneSearch from "../../../models/search/ZoneSearch";
import { auditRunReport } from "../../../services/auditService";
import { getBuildingNamesWithPagination } from "../../../services/buildingService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getControllerNamesWithPagnation } from "../../../services/controllerService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getDoorNamesWithPagination } from "../../../services/doorService";
import { getEnrolmentNamesWithPagination } from "../../../services/enrolmentService";
import { getEventTypeNamesWithPagination } from "../../../services/eventTypeService";
import { getReaderNamesWithPagination } from "../../../services/readerService";
import { getTransactionReport } from "../../../services/reportService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { getZoneNamesWithPagination } from "../../../services/zoneService";
import { IAppState } from "../../../store/types";
import { getTodayDate } from "../../../utilities/DateUtils";
import { appendFilter } from "../../../utilities/filters";

interface DeniedTransactionsProps {
    reportId: string;
}

const DeniedTransactions: FC<DeniedTransactionsProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const lookup: Lookup[] = [
        {
            name: LookupTypes.eventType,
            values: [],
            dataMethod: (listQuery?: ListQuery) =>
                getEventTypeNamesWithPagination({
                    ...listQuery,
                    filters: appendFilter('*Denied*', 'Transack Type Denied', 'etName', listQuery?.filters ?? [])
                }),
            searchAccessor: EventTypeSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.location,
            values: [],
            dataMethod: getDoorNamesWithPagination,
            searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.zone,
            values: [],
            dataMethod: getZoneNamesWithPagination,
            searchAccessor: ZoneSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.building,
            values: [],
            dataMethod: getBuildingNamesWithPagination,
            searchAccessor: BuildingSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.controller,
            values: [],
            dataMethod: getControllerNamesWithPagnation,
            searchAccessor: ControllerSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.terminal,
            values: [],
            dataMethod: getReaderNamesWithPagination,
            searchAccessor: ReaderSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
        },
	    {
            name: LookupTypes.ossDevice,
            values: [],
            dataMethod: getOssDeviceNamesWithPagination,
            searchAccessor: OssDeviceSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.masterByTrMasterId,
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: EnrolmentSearch.find(column => column.accessor === "name")?.searchOnName
        }
    ]

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_TRANSACTION_ALARM;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        // Filters that should always show for this view
        const staticFilterKeys = ["dateOnly"];

        //Add custom filter
        let customFilter: Filter[] = [];
        getUserFiltersByView(userLoginId, currentMenuDetail, reportId).then((response) => {
            const dateFilter = response.find(filter => filter.filterId === FILTER_DATE);
            if (!!!dateFilter) {
                customFilter = [{
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "locked",
                    key: "dateOnly",
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        label: getTodayDate(),
                        value: getTodayDate(),
                    }]
                }];
            }
        }).catch((error) => {
            console.log("Failed to load user preferences")
        }).finally(() => {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
        })
    }
    // <-- end context

    const getReportFilters = (currentFilters) => {

        // Default to today's date
        let filters = appendFilter(`*${getTodayDate()}*`, "Date/Time", "dateOnly", currentFilters);

        // Limit to denied type transactions only.
        return appendFilter('*Denied*', 'Transack Type Denied', 'eventType.etName', filters);
    }

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getTransactionReport(user.profileId, translate, { ...listParameters, filters: getReportFilters(listParameters?.filters) });
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return <TableContextProvider
        maxPageSize={DEF_PG_SIZE_REPORTS}
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={TransactionColumn}
        searchDescriptor={TransactionSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}
        loadDataOnDemand
        runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.transactionsDenied)}
            selectRows={false}
            showFilterMenu={true}
            cyName="deniedTransactionsReport" />
    </TableContextProvider >
};

export default DeniedTransactions;

